import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LifeBuoy, Search, RefreshCw, AlertTriangle, Clock, CheckCircle2,
  MessageSquare, UserPlus, Send, Lock,
} from 'lucide-react';
import MainLayout from '../components/MainLayout';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  open: 'bg-slate-100 text-slate-700',
  assigned: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  waiting_on_employee: 'bg-purple-100 text-purple-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-200 text-gray-700',
  reopened: 'bg-rose-100 text-rose-700',
};

const PRIORITY_BADGE = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return s; } };

const slaCountdown = (deadline) => {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms < 0) return { overdue: true, label: `Overdue ${Math.ceil(-ms / 3600000)}h` };
  const hrs = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  return { overdue: false, label: `${hrs}h ${mins}m` };
};

export default function HelpDeskTracker() {
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ categories: [], priorities: [], statuses: [] });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', category: '', priority: '', q: '', assigned_to_me: false });
  const [admins, setAdmins] = useState([]);
  const [selected, setSelected] = useState(null);



  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s, a] = await Promise.all([
        axios.get(`${API_URL}/helpdesk/categories`, { withCredentials: true }),
        axios.get(`${API_URL}/helpdesk/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/helpdesk/admins`, { withCredentials: true }),
      ]);
      setMeta(m.data); setStats(s.data); setAdmins(a.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ scope: 'all' });
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.q) params.append('q', filters.q);
      if (filters.assigned_to_me) params.append('assigned_to_me', 'true');
      const { data } = await axios.get(`${API_URL}/helpdesk/tickets?${params}`, { withCredentials: true });
      setTickets(data.tickets || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load tickets'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadTickets(); }, [filters, loadTickets]);

  const refreshAll = () => { loadAll(); loadTickets(); };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="helpdesk-tracker-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <LifeBuoy className="text-amber-600" size={28} /> Help Desk Tracker
          </h1>
          <p className="text-gray-600 mt-1">Manage employee tickets — payroll, attendance, system, HR.</p>
        </div>
        <button onClick={refreshAll} className="text-slate-700 hover:text-slate-900 flex items-center gap-1 text-sm">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KPI label="Open Tickets" value={stats.open} color="blue" data-testid="kpi-open" />
          <KPI label="Overdue (SLA)" value={stats.overdue} color="red" icon={<AlertTriangle size={16} />} data-testid="kpi-overdue" />
          <KPI label="Resolved" value={stats.resolved} color="emerald" icon={<CheckCircle2 size={16} />} data-testid="kpi-resolved" />
          <KPI label="Avg Resolution (90d)" value={stats.avg_resolution_hours_90d != null ? `${stats.avg_resolution_hours_90d}h` : '—'} color="amber" icon={<Clock size={16} />} data-testid="kpi-avg" />
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-slate-400" />
            <input value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search ticket #, subject, employee…"
              data-testid="tracker-search"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm" />
          </div>
          <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}
            data-testid="tracker-filter-status"
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Statuses</option>
            {meta.statuses?.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
          <select value={filters.category} onChange={e => setFilters({ ...filters, category: e.target.value })}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Categories</option>
            {meta.categories?.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Priorities</option>
            {meta.priorities?.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={filters.assigned_to_me}
              data-testid="tracker-assigned-to-me"
              onChange={e => setFilters({ ...filters, assigned_to_me: e.target.checked })} />
            Assigned to me only
          </label>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading && <div className="p-6 text-center text-gray-500">Loading…</div>}
        {!loading && tickets.length === 0 && <div className="p-10 text-center text-gray-500">No tickets match current filters.</div>}
        {!loading && tickets.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Ticket #</th>
                <th className="text-left px-4 py-3 font-medium">Subject</th>
                <th className="text-left px-4 py-3 font-medium">Raised by</th>
                <th className="text-left px-4 py-3 font-medium">Category</th>
                <th className="text-left px-4 py-3 font-medium">Priority</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Assignee</th>
                <th className="text-left px-4 py-3 font-medium">SLA</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => {
                const sla = slaCountdown(t.sla_deadline);
                const overdue = t.is_overdue || sla?.overdue;
                return (
                  <tr key={t.id} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${overdue && !['resolved','closed'].includes(t.status) ? 'bg-red-50/40' : ''}`}
                      onClick={() => setSelected(t)} data-testid={`tracker-row-${t.ticket_number}`}>
                    <td className="px-4 py-3 font-mono text-blue-700">{t.ticket_number}</td>
                    <td className="px-4 py-3 font-medium max-w-xs truncate">{t.subject}</td>
                    <td className="px-4 py-3 text-slate-600">{t.raised_by_name}</td>
                    <td className="px-4 py-3 capitalize text-slate-600">{t.category.replace('_', ' ')}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_BADGE[t.priority]}`}>{t.priority}</span></td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[t.status]}`}>{t.status.replace(/_/g, ' ')}</span></td>
                    <td className="px-4 py-3 text-slate-600">{t.assigned_to_name || '—'}</td>
                    <td className="px-4 py-3">
                      {['resolved', 'closed'].includes(t.status)
                        ? <span className="text-emerald-700 text-xs flex items-center gap-1"><CheckCircle2 size={12}/> Done</span>
                        : overdue
                          ? <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold flex items-center gap-1 w-fit"><AlertTriangle size={12}/> {sla?.label || 'Overdue'}</span>
                          : <span className="text-slate-600 text-xs flex items-center gap-1"><Clock size={12}/> {sla?.label}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && <AdminTicketDetail t={selected} admins={admins} onClose={() => setSelected(null)} onChanged={refreshAll} />}
      </div>
    </MainLayout>
  );
}

function KPI({ label, value, color, icon, ...rest }) {
  const palette = {
    blue: 'from-blue-50 to-white border-blue-200 text-blue-700',
    red: 'from-red-50 to-white border-red-200 text-red-700',
    emerald: 'from-emerald-50 to-white border-emerald-200 text-emerald-700',
    amber: 'from-amber-50 to-white border-amber-200 text-amber-700',
  }[color] || 'from-slate-50 to-white border-slate-200 text-slate-700';
  return (
    <div className={`bg-gradient-to-br ${palette} border rounded-xl p-4`} {...rest}>
      <div className="text-xs font-medium uppercase tracking-wide flex items-center gap-1">{icon}{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function AdminTicketDetail({ t, admins, onClose, onChanged }) {
  const [ticket, setTicket] = useState(t);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [showResolve, setShowResolve] = useState(false);
  const [assigneeId, setAssigneeId] = useState(t.assigned_to_user_id || '');

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/helpdesk/tickets/${t.id}`, { withCredentials: true });
      setTicket(data); setAssigneeId(data.assigned_to_user_id || '');
    } catch { /* */ }
  }, [t.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await axios.post(`${API_URL}/helpdesk/tickets/${ticket.id}/comments`,
        { message: reply, is_internal: internal }, { withCredentials: true });
      setReply(''); setInternal(false);
      await refresh(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reply failed'); }
  };

  const updateStatus = async (status) => {
    if (status === 'resolved' && !resolutionNote.trim()) { setShowResolve(true); return; }
    try {
      await axios.put(`${API_URL}/helpdesk/tickets/${ticket.id}/status`,
        { status, resolution_note: status === 'resolved' || status === 'closed' ? resolutionNote : '' },
        { withCredentials: true });
      toast.success(`Ticket ${status}`);
      setShowResolve(false); setResolutionNote('');
      await refresh(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Status update failed'); }
  };

  const reassign = async () => {
    if (!assigneeId || assigneeId === ticket.assigned_to_user_id) return;
    try {
      await axios.put(`${API_URL}/helpdesk/tickets/${ticket.id}/assign`,
        { user_id: assigneeId }, { withCredentials: true });
      toast.success('Reassigned');
      await refresh(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reassign failed'); }
  };

  const isResolved = ['resolved', 'closed'].includes(ticket.status);
  const sla = slaCountdown(ticket.sla_deadline);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto"
           onClick={e => e.stopPropagation()} data-testid="admin-ticket-detail-modal">
        <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs font-mono text-blue-700">{ticket.ticket_number}</div>
              <h2 className="text-xl font-bold mt-1">{ticket.subject}</h2>
              <div className="flex gap-2 mt-2 items-center flex-wrap">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[ticket.status]}`}>{ticket.status.replace(/_/g, ' ')}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_BADGE[ticket.priority]}`}>{ticket.priority}</span>
                <span className="text-xs text-slate-500 capitalize">{ticket.category.replace('_', ' ')}{ticket.sub_category ? ` · ${ticket.sub_category}` : ''}</span>
                {!isResolved && sla && (
                  <span className={`text-xs font-medium ${sla.overdue ? 'text-red-700' : 'text-slate-600'}`}>
                    {sla.overdue ? <AlertTriangle size={12} className="inline" /> : <Clock size={12} className="inline" />} {sla.label}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-2">
                Raised by <b>{ticket.raised_by_name}</b> ({ticket.raised_by_email}) · {fmtDate(ticket.created_at)}
                {ticket.related_month && <> · Related month <b>{ticket.related_month}</b></>}
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* LEFT: thread */}
          <div className="md:col-span-2 space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="text-xs text-slate-500 mb-1">{ticket.raised_by_name} · {fmtDate(ticket.created_at)}</div>
              <div className="text-sm whitespace-pre-wrap">{ticket.description}</div>
            </div>

            {(ticket.comments || []).map(c => (
              <div key={c.id} className={`rounded-lg p-3 ${c.is_internal ? 'bg-yellow-50 border-l-4 border-yellow-400' : c.is_admin ? 'bg-blue-50 border-l-4 border-blue-400' : 'bg-emerald-50 border-l-4 border-emerald-400'}`}>
                <div className="text-xs text-slate-600 mb-1 flex items-center gap-2">
                  <b>{c.user_name}</b> {c.is_admin ? `(${c.role})` : '(Employee)'}
                  {c.is_internal && <span className="text-yellow-700 flex items-center gap-1"><Lock size={10}/> Internal note</span>}
                  · {fmtDate(c.created_at)}
                </div>
                <div className="text-sm whitespace-pre-wrap">{c.message}</div>
              </div>
            ))}

            {!isResolved && (
              <div className="border border-slate-200 rounded-lg p-3">
                <textarea rows={3} value={reply} onChange={e => setReply(e.target.value)}
                  data-testid="admin-reply-input"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2"
                  placeholder="Reply to the employee or add an internal note…" />
                <div className="flex justify-between items-center mt-2">
                  <label className="text-xs text-slate-600 flex items-center gap-1">
                    <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)}
                      data-testid="admin-internal-checkbox" />
                    <Lock size={12} /> Internal note (employee won't see)
                  </label>
                  <button onClick={sendReply} disabled={!reply.trim()}
                    data-testid="admin-reply-btn"
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-50 flex items-center gap-1">
                    <Send size={14} /> Send {internal ? 'Note' : 'Reply'}
                  </button>
                </div>
              </div>
            )}

            {ticket.rating && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <b>Employee Rating:</b> {ticket.rating}/5 {ticket.rating_comment && `— "${ticket.rating_comment}"`}
              </div>
            )}
          </div>

          {/* RIGHT: actions */}
          <div className="space-y-4">
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1"><UserPlus size={12} /> Assignee</div>
              <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)}
                data-testid="admin-assignee-select"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="">— unassigned —</option>
                {admins.map(a => <option key={a.id} value={a.id}>{`${a.name} (${a.role})`}</option>)}
              </select>
              {assigneeId !== ticket.assigned_to_user_id && (
                <button onClick={reassign} className="mt-2 w-full bg-slate-900 text-white rounded-lg py-1.5 text-sm" data-testid="admin-reassign-btn">
                  Save Assignee
                </button>
              )}
            </div>

            {!isResolved && (
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1"><MessageSquare size={12} /> Status</div>
                <div className="flex flex-col gap-2">
                  {['in_progress', 'waiting_on_employee'].map(s => (
                    <button key={s} onClick={() => updateStatus(s)}
                      data-testid={`admin-status-${s}`}
                      className="w-full text-left text-sm bg-slate-100 hover:bg-slate-200 rounded px-3 py-1.5">
                      Mark <b>{s.replace(/_/g, ' ')}</b>
                    </button>
                  ))}
                  <button onClick={() => setShowResolve(true)} data-testid="admin-status-resolved"
                    className="w-full text-left text-sm bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded px-3 py-1.5 font-medium">
                    Resolve
                  </button>
                  <button onClick={() => updateStatus('closed')} data-testid="admin-status-closed"
                    className="w-full text-left text-sm bg-slate-200 hover:bg-slate-300 text-slate-800 rounded px-3 py-1.5">
                    Close
                  </button>
                </div>
              </div>
            )}

            {showResolve && (
              <div className="border border-emerald-300 bg-emerald-50 rounded-lg p-3">
                <div className="text-sm font-medium text-emerald-800 mb-2">Resolution note (visible to employee)</div>
                <textarea rows={3} value={resolutionNote} onChange={e => setResolutionNote(e.target.value)}
                  data-testid="admin-resolution-note"
                  className="w-full border border-emerald-300 rounded px-2 py-1.5 text-sm" />
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setShowResolve(false)} className="flex-1 bg-slate-100 rounded py-1 text-sm">Cancel</button>
                  <button onClick={() => updateStatus('resolved')} disabled={!resolutionNote.trim()}
                    data-testid="admin-confirm-resolve-btn"
                    className="flex-1 bg-emerald-600 text-white rounded py-1 text-sm disabled:opacity-50">Confirm Resolve</button>
                </div>
              </div>
            )}

            <div className="border border-slate-200 rounded-lg p-3 text-xs space-y-1">
              <div className="uppercase tracking-wide text-slate-500 mb-1">Activity Log</div>
              {(ticket.activity_log || []).slice().reverse().map((a, i) => (
                <div key={i} className="text-slate-600 border-l-2 border-slate-200 pl-2">
                  <div><b>{a.action}</b> by {a.by_name}</div>
                  <div className="text-slate-400">{fmtDate(a.at)}</div>
                  {a.details && <div className="text-slate-500 italic">{a.details}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
